import { useState, useEffect } from 'react'
import { api } from '../api'
import { AdminTab, PlatformAdmin, Tenant } from '../types'

interface Props {
  tenants: Tenant[]
  platformAdmins: PlatformAdmin[]
  tenantAdminsCount: number
  onNavigate: (tab: AdminTab) => void
  onOpenCreateTenant: () => void
  onOpenCreatePlatformAdmin: () => void
}

export function OverviewView({
  tenants,
  platformAdmins,
  tenantAdminsCount,
  onNavigate,
  onOpenCreateTenant,
  onOpenCreatePlatformAdmin,
}: Props) {
  const [healthStatus, setHealthStatus] = useState<string>('Kontrol ediliyor…')
  const [healthOk, setHealthOk] = useState<boolean | null>(null)

  useEffect(() => {
    api
      .getHealth()
      .then((res) => {
        setHealthStatus(res.status || 'Aktif (Healthy)')
        setHealthOk(true)
      })
      .catch(() => {
        setHealthStatus('Bağlantı Hatası')
        setHealthOk(false)
      })
  }, [])

  const activeTenants = tenants.filter((t) => t.is_active).length
  const poolTenants = tenants.filter((t) => t.tenant_type === 'pool').length
  const siloTenants = tenants.filter((t) => t.tenant_type === 'silo').length
  const activePlatformAdmins = platformAdmins.filter((a) => a.is_active).length

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="view-title-block">
          <h2>Platform Genel Bakış</h2>
          <p>Çok kiracılı ERP altyapısı, tenant dağılımları ve platform yönetici istatistikleri</p>
        </div>
        <div className="view-actions">
          <button type="button" className="btn btn-secondary" onClick={onOpenCreatePlatformAdmin}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Platform Admin Ekle</span>
          </button>
          <button type="button" className="btn btn-primary" onClick={onOpenCreateTenant}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Yeni Tenant Oluştur</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Toplam Tenant</span>
            <span className="stat-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-3" />
              </svg>
            </span>
          </div>
          <div className="stat-value">{tenants.length}</div>
          <div className="stat-subtext">
            {activeTenants} aktif, {tenants.length - activeTenants} pasif
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Mimari Dağılımı</span>
            <span className="stat-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
              </svg>
            </span>
          </div>
          <div className="stat-value" style={{ display: 'flex', gap: '12px', alignItems: 'baseline' }}>
            <span style={{ color: 'var(--color-pool)' }}>{poolTenants} <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>Pool</span></span>
            <span style={{ color: 'var(--color-silo)' }}>{siloTenants} <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>Silo</span></span>
          </div>
          <div className="stat-subtext">Çok kiracılı hibrit veri tabanı mimarisi</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Tenant Yöneticileri</span>
            <span className="stat-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
            </span>
          </div>
          <div className="stat-value">{tenantAdminsCount}</div>
          <div className="stat-subtext">Kayıtlı TenantAdmin hesabı</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Platform Adminler</span>
            <span className="stat-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
          </div>
          <div className="stat-value">{platformAdmins.length}</div>
          <div className="stat-subtext">{activePlatformAdmins} aktif süper yönetici</div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">API / Veri Tabanı Durumu</span>
            <span className="stat-icon" aria-hidden="true">
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: healthOk ? '#10b981' : '#ef4444',
                }}
              />
            </span>
          </div>
          <div className="stat-value" style={{ fontSize: '1.25rem', color: healthOk ? '#059669' : '#dc2626' }}>
            {healthStatus}
          </div>
          <div className="stat-subtext">FastAPI Backend Servisi</div>
        </div>
      </div>

      {/* Recent Tenants Section */}
      <div className="toolbar-card" style={{ marginTop: '8px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>Son Oluşturulan Tenantlar</h3>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onNavigate('tenants')}
        >
          Tüm Tenantları Gör ({tenants.length})
        </button>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Tenant Adı</th>
              <th>Slug</th>
              <th>Mimari</th>
              <th>Hesaplama Yöntemi</th>
              <th>Durum</th>
              <th style={{ textAlign: 'right' }}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-state">
                    <p>Henüz kayıtlı bir tenant bulunmamaktadır.</p>
                    <button type="button" className="btn btn-primary btn-sm" onClick={onOpenCreateTenant}>
                      İlk Tenantı Oluştur
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              tenants.slice(0, 5).map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td>
                    <code>{t.slug}</code>
                  </td>
                  <td>
                    <span className={`badge badge-${t.tenant_type}`}>
                      {t.tenant_type.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    {t.default_calculation_method === 'cost_plus'
                      ? 'Maliyet Üzerinden (Cost-Plus)'
                      : 'Satış Fiyatı Üzerinden (Margin)'}
                  </td>
                  <td>
                    <span className={`badge badge-${t.is_active ? 'active' : 'inactive'}`}>
                      {t.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => onNavigate('tenants')}
                    >
                      Yönet
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
